import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  ListItemText,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  Divider
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Add, Delete, CloudUpload } from "@material-ui/icons";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CREATE_SERVICE_REQUEST } from "../../../../graphql/serviceRequests/createServiceRequestMutation";
import { GET_VEHICLE } from "../../../../graphql/vehicles/getVehicle";
import { GET_VEHICLES_FOR_BUSINESS_CUSTOMER, GET_VEHICLES_FOR_INDIVIDUAL_CUSTOMER } from "../../../../graphql/vehicles/getVehicles";
import { GET_SERVICE_REQUEST_QUESTIONS } from "../../../../graphql/serviceRequests/serviceRequestQuestionsQuery";
import { GET_SPECIFIC_BRANCHES } from "../../../../graphql/bookings/getSpecificBranches";
import { GET_SERVICE_TEMPLATES_BY_SERVICE_CATEGORY } from "../../../../graphql/serviceTemplates/getServiceTemplatesQuery";
import { GET_ALL_SERVICE_TASK_ITEMS } from "../../../../graphql/serviceRequests/getAllServiceTaskListQuery";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { IAppState } from "../../../../store";
import * as UUID from "uuid";
import { returnfileContentTypeAndExtention } from "../../../utils";
import { getSignedUrl } from "../../../../utils/getSignedUrl";
import _ from "lodash";
import { UserRole } from "../../../../reducers/auth/types";

interface Issue {
  issueType: string;
  description: string;
  images: string[];
}

interface JobSheetItem {
  id?: string;
  task: string;
  taskDescription?: string;
  cost: number;
  source?: "TEMPLATE" | "MANUAL";
}

interface IServiceTaskItem {
  id: string;
  name: string;
  cost: number;
  description: string;
  type: string;
}

interface IServiceRequestQuestionConfig {
  id: string;
  question: string;
  flow: string;
  answerType: string;
  options?: string[];
  isMandatory?: boolean;
  helpText?: string;
  isActive?: boolean;
  requestTypes?: string[];
  serviceCategories?: string[];
  index?: number;
}

interface IServiceCategoryOption {
  value: string;
  label: string;
  key?: string; 
}

enum ServiceRequestType {
  REGULAR = "REGULAR"
}

const QUESTION_FLOW_LABELS: Record<string, string> = {
  SERVICE_REQUEST: "Service Details",
  ACCIDENT_DAMAGE: "Accident / Damage Information",
  DOCUMENT_COLLECTION: "Document Uploads"
};

const REQUEST_TYPE_FLOW_MAP: Record<string, string[]> = {
  [ServiceRequestType.REGULAR]: ["SERVICE_REQUEST", "ACCIDENT_DAMAGE", "DOCUMENT_COLLECTION"],
  "AD-HOC": ["SERVICE_REQUEST", "ACCIDENT_DAMAGE", "DOCUMENT_COLLECTION"],
  ADHOC: ["SERVICE_REQUEST", "ACCIDENT_DAMAGE", "DOCUMENT_COLLECTION"],
  EMERGENCY: ["SERVICE_REQUEST", "ACCIDENT_DAMAGE", "DOCUMENT_COLLECTION"],
  ACCIDENT: ["SERVICE_REQUEST", "ACCIDENT_DAMAGE", "DOCUMENT_COLLECTION"]
};

const DEFAULT_SERVICE_TYPES: IServiceCategoryOption[] = [
  { value: "Regular", label: "Regular", key: "REGULAR" },
  { value: "Ad-Hoc", label: "Ad-Hoc", key: "AD-HOC" },
  { value: "Emergency", label: "Emergency", key: "EMERGENCY" }
];

const DEFAULT_SERVICE_CATEGORIES: IServiceCategoryOption[] = [
  { value: "SERVICE", label: "Service" },
  { value: "MECHANICAL", label: "Mechanical" },
  { value: "BODY_REPAIRS", label: "Body Repairs" },
  { value: "TYRES", label: "Tyres" },
  { value: "MOT", label: "MOT" }
];

const DEFAULT_REGULAR_CATEGORIES: IServiceCategoryOption[] = [
  { value: ServiceRequestType.REGULAR, label: "Regular" }
];

const CreateServiceRequest: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const showSnackbar = useSnackBar();
  const { t } = useTranslation();
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const { _e_ } = useSelector((state: IAppState) => state.authReducer);  
  const consumerWebsite = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const queryParams = new URLSearchParams(location.search);
  const vehicleIdFromUrl = queryParams.get("vehicleId");
  const serviceTypeFromUrl = queryParams.get("serviceType");
  const serviceCategoryFromUrl = queryParams.get("serviceCategory");
  
  const [formData, setFormData] = useState<{
    vehicle: { id: string; licencePlate?: string } | null;
    serviceType: string;
    serviceCategory: string;
    comments: string;
  }>({
    vehicle: null,
    serviceType: "",
    serviceCategory: "",
    comments: ""
  });

  const [issues, setIssues] = useState<Issue[]>([
    { issueType: "", description: "", images: [] }
  ]);

  const [jobSheetItems, setJobSheetItems] = useState<JobSheetItem[]>([]);
  const [templatesPopulated, setTemplatesPopulated] = useState(false);
  const templateFetchKeyRef = useRef<string>("");
  const [getServiceTemplates, { data: templatesData, loading: loadingTemplates }] = useLazyQuery(
    GET_SERVICE_TEMPLATES_BY_SERVICE_CATEGORY,
    {
      fetchPolicy: "network-only"
    }
  );
  const [serviceTasks, setServiceTasks] = useState<IServiceTaskItem[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<{ [key: number]: string }>({});
  const [loadServiceTasks, { loading: loadingServiceTasks, data: serviceTaskData }] = useLazyQuery(
    GET_ALL_SERVICE_TASK_ITEMS,
    {
      fetchPolicy: "network-only"
    }
  );

  const [createServiceRequest, { loading }] = useMutation(CREATE_SERVICE_REQUEST);
  const [uploadingImages, setUploadingImages] = useState<Record<number, boolean>>({});
  const [imageUrlMap, setImageUrlMap] = useState<Record<string, string>>({});
  const [questionFileUrlMap, setQuestionFileUrlMap] = useState<Record<string, string>>({});
  const [loadQuestions, { data: questionsData, loading: questionsLoading }] = useLazyQuery(
    GET_SERVICE_REQUEST_QUESTIONS,
    {
      fetchPolicy: "network-only",
      onError: (error) => {
        showSnackbar({
          message: error.message,
          variant: SnackBarVariant.ERROR
        });
      }
    }
  );
  const normalizeRequestType = (type: string): string => {
    return type.trim().toUpperCase().replace(/[-\s]/g, "");
  };

  const doesQuestionMatchCategory = (
    question: IServiceRequestQuestionConfig,
    normalizedCategory: string
  ) => {
    if (!normalizedCategory) {
      return true;
    }
    if (!question.serviceCategories || question.serviceCategories.length === 0) {
      return true;
    }
    return question.serviceCategories.some(
      (category) => category.trim().toUpperCase() === normalizedCategory
    );
  };
  const [questionResponses, setQuestionResponses] = useState<Record<string, { answer?: string; selectedOptions?: string[]; files?: string[] }>>({});
  const [questionUploadStatus, setQuestionUploadStatus] = useState<Record<string, boolean>>({});
  const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>();
  
  const { data: vehicleData, loading: vehicleLoading } = useQuery(GET_VEHICLE, {
    variables: { vehicleId: vehicleIdFromUrl },
    skip: !vehicleIdFromUrl,
    fetchPolicy: "cache-and-network"
  });

  const branchIds = useMemo(() => consumerWebsite?.branchIds || [], [consumerWebsite?.branchIds]);
  const appSettingId = consumerWebsite?.id;

  const [fetchBranchData, { data: branchData, loading: branchLoading }] = useLazyQuery(
    GET_SPECIFIC_BRANCHES,
    {
      fetchPolicy: "network-only",
      onError: (error) => {
        showSnackbar({
          message: error.message,
          variant: SnackBarVariant.ERROR
        });
      }
  });

  const isBusinessCustomer = !!user?.businesscustomers?.id;
  const { data: vehiclesData, loading: vehiclesLoading } = useQuery(
    isBusinessCustomer ? GET_VEHICLES_FOR_BUSINESS_CUSTOMER : GET_VEHICLES_FOR_INDIVIDUAL_CUSTOMER,
    {
      variables: isBusinessCustomer 
        ? { businessCustomerId: user?.businesscustomers?.id, driver : user?.role === UserRole.DRIVER ? user?.customerId : undefined}
        : { customerId: user?.customerId },
      skip: !!vehicleIdFromUrl,
      fetchPolicy: "cache-and-network"
    }
  );

  const availableVehicles = useMemo(() => {
    return isBusinessCustomer
      ? vehiclesData?.getVehiclesForBusinessCustomer || []
      : vehiclesData?.getVehiclesForIndividualCustomer || [];
  }, [isBusinessCustomer, vehiclesData]);

  const vehicleBranchId = useMemo(() => {
    if (formData.vehicle?.id) {
      const selectedVehicle = availableVehicles.find((v: any) => v.id === formData.vehicle?.id);
      if (selectedVehicle?.branchDetails?.id) {
        return selectedVehicle.branchDetails.id;
      }
      if (selectedVehicle?.branch) {
        return selectedVehicle.branch;
      }
    }
    if (vehicleData?.vehicle?.branchDetails?.id) {
      return vehicleData.vehicle.branchDetails.id;
    }
    if (vehicleData?.vehicle?.branch) {
      return vehicleData.vehicle.branch;
    }
    return selectedBranchId;
  }, [formData.vehicle?.id, availableVehicles, vehicleData?.vehicle, selectedBranchId]);

  const serviceTypeOptions: IServiceCategoryOption[] = useMemo(() => {
    const branchesWithSettings =
      branchData?.getSpecificBranches ||
      [];
    const branchIdToUse = vehicleBranchId || selectedBranchId;
    const selectedBranch =
      branchesWithSettings.find((branch: any) => branch.id === branchIdToUse) ||
      consumerWebsite?.branches?.find((branch: any) => branch.id === branchIdToUse);

    const configuredTypes = selectedBranch?.serviceSettings?.serviceTypes || [];
    if (configuredTypes.length) {
      return configuredTypes
        .filter((item: any) => item && item.key && item.label)
        .map((item: any) => ({
          value: item.label,
          label: item.label,
          key: item.key 
        }));
    }

    return DEFAULT_SERVICE_TYPES;
  }, [branchData, consumerWebsite?.branches, vehicleBranchId, selectedBranchId]);

  const selectedServiceType = serviceTypeOptions.find(
    (option) => option.value === formData.serviceType
  );
  
  const normalizedServiceType = formData.serviceType
    ? selectedServiceType?.key 
      ? selectedServiceType.key.toUpperCase()
      : formData.serviceType.replace(/\s+/g, "-").toUpperCase()
    : "";
  const normalizedServiceCategory = formData.serviceCategory
    ? formData.serviceCategory.trim().toUpperCase()
    : "";
  const isServiceCategoryRequired =
    normalizedServiceType &&
    normalizedServiceType !== ServiceRequestType.REGULAR;

  const serviceCategoryOptions: IServiceCategoryOption[] = useMemo(() => {
    const branchesWithSettings =
      branchData?.getSpecificBranches ||
      [];
    const branchIdToUse = vehicleBranchId || selectedBranchId;
    const selectedBranch =
      branchesWithSettings.find((branch: any) => branch.id === branchIdToUse) ||
      consumerWebsite?.branches?.find((branch: any) => branch.id === branchIdToUse);

    const isRegular = normalizedServiceType === ServiceRequestType.REGULAR;
    const configuredCategories = isRegular 
      ? (selectedBranch?.serviceSettings?.regularServiceCategories || [])
      : (selectedBranch?.serviceSettings?.serviceCategories || []);
    
    if (configuredCategories.length) {
      const mappedCategories = configuredCategories
        .filter((item: any) => item && item.key && item.label)
        .map((item: any) => ({
          value: item.key,
          label: item.label
        }));
      
      if (isRegular) {
        const hasRegularCategory = mappedCategories.some(
          (cat: any) => cat.value === ServiceRequestType.REGULAR
        );
        if (!hasRegularCategory) {
          mappedCategories.unshift({
            value: ServiceRequestType.REGULAR,
            label: "Regular"
          });
        }
      }
      
      return mappedCategories;
    }

    if (isRegular) {
      return DEFAULT_REGULAR_CATEGORIES;
    }
    return DEFAULT_SERVICE_CATEGORIES;
  }, [branchData, consumerWebsite?.branches, vehicleBranchId, selectedBranchId, normalizedServiceType]);

  const serviceRequestQuestions: IServiceRequestQuestionConfig[] = useMemo(() => {
    if (!questionsData?.serviceRequestQuestions) {
      return [];
    }

    const filtered = [...questionsData.serviceRequestQuestions]
      .filter((q: IServiceRequestQuestionConfig) => {
        if (!q.isActive) {
          return false;
        }
        if (!normalizedServiceType || !q.requestTypes || q.requestTypes.length === 0) {
          return doesQuestionMatchCategory(q, normalizedServiceCategory);
        }
        const normalizedQuestionTypes = q.requestTypes.map((type) => normalizeRequestType(type));
        const matchesType = normalizedQuestionTypes.includes(normalizedServiceType);
        if (!matchesType) {
          return false;
        }
        return doesQuestionMatchCategory(q, normalizedServiceCategory);
      })
      .sort((a, b) => (a.index || 0) - (b.index || 0));
    
    return filtered;
  }, [questionsData, normalizedServiceType, normalizedServiceCategory]);

  const questionsByFlow = useMemo(() => {
    return serviceRequestQuestions.reduce<Record<string, IServiceRequestQuestionConfig[]>>((acc, question) => {
      if (!acc[question.flow]) {
        acc[question.flow] = [];
      }
      acc[question.flow].push(question);
      return acc;
    }, {});
  }, [serviceRequestQuestions]);

  const flowsToRender = useMemo(() => {
    const flows = REQUEST_TYPE_FLOW_MAP[normalizedServiceType] || ["SERVICE_REQUEST", "DOCUMENT_COLLECTION"];
    return flows
      .map((flow) => ({
        key: flow,
        label: QUESTION_FLOW_LABELS[flow] || flow,
        questions: questionsByFlow[flow] || []
      }))
      .filter((item) => item.questions.length > 0);
  }, [normalizedServiceType, questionsByFlow]);

  const isAnyQuestionUploading = useMemo(
    () => Object.values(questionUploadStatus).some(Boolean),
    [questionUploadStatus]
  );

  const isSubmitting = loading || questionsLoading || isAnyQuestionUploading || branchLoading;

  useEffect(() => {
    if (vehicleData?.vehicle) {
      const vehicle = vehicleData.vehicle;
      setFormData(prev => ({
        ...prev,
        vehicle: {
          id: vehicle.id,
          licencePlate: vehicle.licencePlate || ""
        }
      }));
    }
  }, [vehicleData]);

  useEffect(() => {
    if (!formData.vehicle?.id || !serviceTypeFromUrl || formData.serviceType) {
      return;
    }
    const normalizedKey = serviceTypeFromUrl.trim().toUpperCase().replace(/[-\s]/g, "");
    const matchedType = serviceTypeOptions.find((option) => {
      const optionKey = option.key?.toUpperCase().replace(/[-\s]/g, "") || "";
      const optionValue = option.value.toUpperCase().replace(/[-\s]/g, "");
      return optionKey === normalizedKey || optionValue === normalizedKey;
    });
    if (matchedType) {
      setFormData((prev) => ({ ...prev, serviceType: matchedType.value }));
    }
  }, [formData.vehicle?.id, formData.serviceType, serviceTypeFromUrl, serviceTypeOptions]);

  useEffect(() => {
    if (!formData.serviceType || !serviceCategoryFromUrl || formData.serviceCategory) {
      return;
    }
    const normalizedCategory = serviceCategoryFromUrl.trim().toUpperCase();
    const matchedCategory = serviceCategoryOptions.find(
      (option) => option.value.toUpperCase() === normalizedCategory
    );
    if (matchedCategory) {
      setFormData((prev) => ({ ...prev, serviceCategory: matchedCategory.value }));
    }
  }, [formData.serviceType, formData.serviceCategory, serviceCategoryFromUrl, serviceCategoryOptions]);

  useEffect(() => {
    if (vehicleBranchId && appSettingId) {
      fetchBranchData({
        variables: {
          appSettingId,
          branchIds: [vehicleBranchId]
        }
      });
    }
  }, [vehicleBranchId, appSettingId]);

  useEffect(() => {
    if (branchData?.getSpecificBranches?.length && !selectedBranchId) {
      setSelectedBranchId(branchData.getSpecificBranches[0].id);
    }
  }, [branchData, selectedBranchId]);

  useEffect(() => {
    if (!selectedBranchId && consumerWebsite?.branches?.length) {
      setSelectedBranchId(consumerWebsite.branches[0].id);
    }
  }, [consumerWebsite, selectedBranchId]);

  useEffect(() => {
    if (!formData.serviceType) {
      setQuestionResponses({});
      setQuestionUploadStatus({});
      return;
    }

    if (isServiceCategoryRequired && !formData.serviceCategory) {
      setQuestionResponses({});
      setQuestionUploadStatus({});
      return;
    }

    const variables: Record<string, string> = {};

    if (normalizedServiceType) {
      variables.serviceType = normalizedServiceType;
    }

    if (normalizedServiceCategory) {
      variables.serviceCategory = normalizedServiceCategory;
    }

    if (vehicleBranchId) {
      variables.branchId = vehicleBranchId;
    }

    loadQuestions({
      variables
    });
    setQuestionResponses({});
    setQuestionUploadStatus({});
  }, [formData.serviceType, formData.serviceCategory, normalizedServiceType, normalizedServiceCategory, isServiceCategoryRequired, vehicleBranchId, loadQuestions]);

  useEffect(() => {
    if (
      formData.serviceCategory &&
      !serviceCategoryOptions.some((option) => option.value === formData.serviceCategory)
    ) {
      setFormData((prev) => ({ ...prev, serviceCategory: "" }));
    }
  }, [serviceCategoryOptions, formData.serviceCategory]);

  useEffect(() => {
    if (formData.serviceType || formData.serviceCategory) {
      setTemplatesPopulated(false);
    }
  }, [formData.serviceType, formData.serviceCategory, normalizedServiceType, vehicleBranchId]);

  useEffect(() => {
    if ((user?.tenantId || consumerWebsite?.id) && vehicleBranchId) {
      loadServiceTasks({
        variables: {
          branchId: vehicleBranchId
        }
      });
    }
  }, [user?.tenantId, consumerWebsite?.id, vehicleBranchId, loadServiceTasks]);

  useEffect(() => {
    if (serviceTaskData && serviceTaskData.consumerGetAllVehicleServiceTask) {
      const allServiceTasks = [
        ...(serviceTaskData.consumerGetAllVehicleServiceTask.customTasks || []),
        ...(serviceTaskData.consumerGetAllVehicleServiceTask.defaultTasks || [])
      ];
      setServiceTasks(allServiceTasks.map((item: any) => {
        return {
          id: item.id,
          name: item.name,
          cost: item.cost,
          description: item.description,
          type: item.itemType
        };
      }));
    } else if (serviceTaskData && !serviceTaskData.consumerGetAllVehicleServiceTask) {
      setServiceTasks([]);
    }
  }, [serviceTaskData]);

  useEffect(() => {
    if (
      normalizedServiceType === ServiceRequestType.REGULAR && formData.serviceCategory && !templatesPopulated && vehicleBranchId) {
      const key = `${normalizedServiceType}:${formData.serviceCategory}:${vehicleBranchId}`;
      templateFetchKeyRef.current = key;
      getServiceTemplates({
        variables: {
          serviceType: normalizedServiceType,
          serviceCategory: formData.serviceCategory,
          branchId: vehicleBranchId
        }
      });
    }
  }, [formData.serviceCategory, normalizedServiceType, templatesPopulated, getServiceTemplates, serviceCategoryOptions, vehicleBranchId]);

  useEffect(() => {
    if (templatesData?.serviceTemplatesByServiceCategory && !templatesPopulated && formData.serviceCategory) {
      const currentKey = vehicleBranchId
        ? `${normalizedServiceType}:${formData.serviceCategory}:${vehicleBranchId}`
        : "";
      const fetchKey = templateFetchKeyRef.current;
      if (fetchKey && currentKey && fetchKey !== currentKey) {
        return;
      }
      const templates = templatesData.serviceTemplatesByServiceCategory;
      
      const matchingTemplates = templates.filter((t: any) => t.serviceCategory === formData.serviceCategory);
      
      if (!matchingTemplates.length) {
        return;
      }
      
      const newItems: JobSheetItem[] = [];
      matchingTemplates.forEach((template: any) => {
        if (template.tasks?.length) {
          template.tasks.forEach((task: any) => {
            const id = UUID.v4();
            newItems.push({
              id,
              task: task.name || "",
              taskDescription: task.description || "",
              cost: (task.cost || 0) / 100,
              source: "TEMPLATE"
            });
          });
        }
      });
      
      if (newItems.length) {
        setJobSheetItems(prev => {
          const filteredOld = prev.filter(item => item.source !== "TEMPLATE");
          const next = [...newItems, ...filteredOld];
          const newSelectedTasks: { [key: number]: string } = {};
          next.forEach((item, idx) => {
            if (item.task) {
              newSelectedTasks[idx] = item.task;
            }
          });
          setSelectedTasks(newSelectedTasks);
          return next;
        });
      }
      setTemplatesPopulated(true);
    }
  }, [templatesData, templatesPopulated, formData.serviceCategory, normalizedServiceType, vehicleBranchId]);

  const handleAddIssue = () => {
    setIssues([...issues, { issueType: "", description: "", images: [] }]);
  };

  const handleRemoveIssue = (index: number) => {
    if (issues.length > 1) {
      const newIssues = issues.filter((_, i) => i !== index);
      setIssues(newIssues);
    }
  };

  const handleAddJobSheetItem = () => {
    const newIndex = jobSheetItems.length;
    setJobSheetItems([...jobSheetItems, { id: UUID.v4(), task: "", taskDescription: "", cost: 0, source: "MANUAL" }]);
    setSelectedTasks({
      ...selectedTasks,
      [newIndex]: ""
    });
  };

  const handleRemoveJobSheetItem = (index: number) => {
    setJobSheetItems(jobSheetItems.filter((_, i) => i !== index));
    const newSelectedTasks = { ...selectedTasks };
    delete newSelectedTasks[index];
    const reindexedSelectedTasks: { [key: number]: string } = {};
    Object.keys(newSelectedTasks).forEach((key) => {
      const oldIndex = parseInt(key);
      if (oldIndex > index) {
        reindexedSelectedTasks[oldIndex - 1] = newSelectedTasks[oldIndex];
      } else if (oldIndex < index) {
        reindexedSelectedTasks[oldIndex] = newSelectedTasks[oldIndex];
      }
    });
    setSelectedTasks(reindexedSelectedTasks);
  };

  const handleJobSheetItemChange = (index: number, field: keyof JobSheetItem, value: string | number) => {
    const updated = [...jobSheetItems];
    updated[index] = { ...updated[index], [field]: value };
    setJobSheetItems(updated);
  };

  const handleAutoCompleteInputChange = _.debounce((value: string, reason: any, index: number) => {
    if (reason === "input" || reason === "reset") {
      const resourcesToFilter = [...serviceTasks];
      if (value) {
        const matchedTask = resourcesToFilter.find((task) => task.name === value);
        if (matchedTask) {
          handleJobSheetItemChange(index, "task", matchedTask.name);
          handleJobSheetItemChange(index, "taskDescription", matchedTask.description || "");
          handleJobSheetItemChange(index, "cost", (matchedTask.cost || 0) / 100);
        } else {
          handleJobSheetItemChange(index, "task", value);
        }
      }
    }
    if (reason === 'clear') {
      handleJobSheetItemChange(index, "task", "");
      handleJobSheetItemChange(index, "taskDescription", "");
      setSelectedTasks({
        ...selectedTasks,
        [index]: ""
      });
    }
  }, 200);

  const handleIssueChange = (index: number, field: keyof Issue, value: any) => {
    const newIssues = [...issues];
    newIssues[index] = { ...newIssues[index], [field]: value };
    setIssues(newIssues);
  };

  const handleImageUpload = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (!_e_) {
      showSnackbar({
        message: t("root:uploadServiceNotAvailable"),
        variant: SnackBarVariant.ERROR
      });
      return;
    }

    setUploadingImages(prev => ({ ...prev, [index]: true }));
    const imageKeys: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        if (!file.type.startsWith("image/")) {
          throw new Error(t("snackBar:uploadOnlyImageFiles"));
        }

        if (file.size > 5242880) {
          throw new Error(t("snackBar:imageSizeExceed5MB"));
        }

        const { fileExtension } = returnfileContentTypeAndExtention(file);
        const uniqueId = UUID.v4();
        const key = `service-request-images/${uniqueId}.${fileExtension}`;
        
        await _e_.add({
          name: key,
          file: file,
          complete: async () => {
            imageKeys.push(key);
          },
          error: (err: any) => {
            throw new Error(`${t("snackBar:uploadFailed")}: ${err}`);
          }
        });
      }

      // save s3 keys
      if (imageKeys.length > 0) {
        handleIssueChange(index, "images", [...issues[index].images, ...imageKeys]);
        showSnackbar({
          message: `${imageKeys.length} ${t("snackBar:imagesUploadedSuccessfully")}`,
          variant: SnackBarVariant.SUCCESS
        });
      }
    } catch (error: any) {
      showSnackbar({
        message: error.message || t("snackBar:failedToUploadImages"),
        variant: SnackBarVariant.ERROR
      });
    } finally {
      setUploadingImages(prev => ({ ...prev, [index]: false }));
    }
  };

  const updateQuestionResponse = (questionId: string, value: Partial<{ answer?: string; selectedOptions?: string[]; files?: string[] }>) => {
    setQuestionResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ...value
      }
    }));
  };

  const handleQuestionFileUpload = async (questionId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (!_e_) {
      showSnackbar({
        message: t("root:uploadServiceNotAvailable"),
        variant: SnackBarVariant.ERROR
      });
      return;
    }

    setQuestionUploadStatus((prev) => ({ ...prev, [questionId]: true }));
    const uploadedKeys: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        const allowedTypes = [
          "image/",
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ];
        const isAllowed =
          allowedTypes.some((type) => file.type.startsWith(type)) ||
          file.name.toLowerCase().endsWith(".pdf") ||
          file.name.toLowerCase().endsWith(".doc") ||
          file.name.toLowerCase().endsWith(".docx");
        if (!isAllowed) {
          throw new Error("Only images or documents (pdf, doc, docx) are allowed.");
        }

        const maxSizeBytes = 10 * 1024 * 1024;
        if (file.size > maxSizeBytes) {
          throw new Error("Maximum file size is 10 MB per file.");
        }

        const { fileExtension } = returnfileContentTypeAndExtention(file);
        const uniqueId = UUID.v4();
        const key = `service-request-answers/${uniqueId}.${fileExtension}`;
        
        await _e_.add({
          name: key,
          file: file,
          complete: async () => {
            uploadedKeys.push(key);
          },
          error: (err: any) => {
            throw new Error(`${t("snackBar:uploadFailed")}: ${err}`);
          }
        });
      }

      if (uploadedKeys.length > 0) {
        setQuestionResponses((prev) => {
          const existingFiles = prev[questionId]?.files || [];
          return {
            ...prev,
            [questionId]: {
              ...prev[questionId],
              files: [...existingFiles, ...uploadedKeys]
            }
          };
        });
        showSnackbar({
          message: `${uploadedKeys.length} ${t("snackBar:imagesUploadedSuccessfully")}`,
          variant: SnackBarVariant.SUCCESS
        });
      }
    } catch (error: any) {
      showSnackbar({
        message: error.message || t("snackBar:failedToUploadImages"),
        variant: SnackBarVariant.ERROR
      });
    } finally {
      setQuestionUploadStatus((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  const handleRemoveQuestionFile = (questionId: string, url: string) => {
    setQuestionResponses((prev) => {
      const existingFiles = prev[questionId]?.files || [];
      return {
        ...prev,
        [questionId]: {
          ...prev[questionId],
          files: existingFiles.filter((file) => file !== url)
        }
      };
    });
  };

  const renderQuestionInput = (question: IServiceRequestQuestionConfig) => {
    const response = questionResponses[question.id] || {};
    switch (question.answerType) {
      case "YES_NO":
        return (
          <RadioGroup
            row
            value={response.answer || ""}
            onChange={(e) => updateQuestionResponse(question.id, { answer: e.target.value })}
          >
            <FormControlLabel value="Yes" control={<Radio color="primary" />} label={t("misc:yes")} />
            <FormControlLabel value="No" control={<Radio color="primary" />} label={t("misc:no")} />
          </RadioGroup>
        );
      case "DROPDOWN":
        return (
          <FormControl fullWidth>
            <Select
              displayEmpty
              value={response.answer || ""}
              onChange={(e) => updateQuestionResponse(question.id, { answer: e.target.value as string })}
            >
              <MenuItem value="">
                <em>{t("root:selectOption")}</em>
              </MenuItem>
              {question.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "MULTI_SELECT":
        return (
          <FormControl fullWidth>
            <Select
              multiple
              displayEmpty
              value={response.selectedOptions || []}
              renderValue={(selected) =>
                (selected as string[]).length
                  ? (selected as string[]).join(", ")
                  : t("root:selectMultiple")
              }
              onChange={(e) => {
                const value = typeof e.target.value === "string" ? e.target.value.split(",") : (e.target.value as string[]);
                updateQuestionResponse(question.id, {
                  selectedOptions: value,
                  answer: value.join(", ")
                });
              }}
            >
              {question.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox
                    color="primary"
                    checked={(response.selectedOptions || []).indexOf(option) > -1}
                  />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "FILE_UPLOAD":
        return (
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={
                questionUploadStatus[question.id] ? <CircularProgress size={18} /> : <CloudUpload />
              }
              disabled={questionUploadStatus[question.id]}
            >
              {questionUploadStatus[question.id] ? t("root:uploading") : "Upload file(s)"}
              <input
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                hidden
                multiple
                onChange={(e) => handleQuestionFileUpload(question.id, e)}
              />
            </Button>
            {response.files && response.files.length > 0 && (
              <Box mt={1.5} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                {response.files.map((fileKey) => {
                  // load signedUrl
                  if (!questionFileUrlMap[fileKey]) {
                    getSignedUrl(fileKey).then((url) => {
                      if (url) {
                        setQuestionFileUrlMap(prev => ({ ...prev, [fileKey]: url }));
                      }
                    });
                  }
                  return (
                    <Chip
                      key={fileKey}
                      label={t("root:view")}
                      onClick={async () => {
                        // get signedUrl for  s3 key
                        const signedUrl = questionFileUrlMap[fileKey] || await getSignedUrl(fileKey);
                        if (signedUrl) {
                          setQuestionFileUrlMap(prev => ({ ...prev, [fileKey]: signedUrl }));
                          window.open(signedUrl, "_blank");
                        }
                      }}
                      onDelete={() => {
                        handleRemoveQuestionFile(question.id, fileKey);
                        setQuestionFileUrlMap(prev => {
                          const updated = { ...prev };
                          delete updated[fileKey];
                          return updated;
                        });
                      }}
                      color="primary"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        );
      default:
        return (
          <TextField
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            placeholder={t("root:typeYourResponse")}
            value={response.answer || ""}
            onChange={(e) => updateQuestionResponse(question.id, { answer: e.target.value })}
          />
        );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.vehicle?.id) {
      showSnackbar({ message: t("snackBar:pleaseSelectVehicle"), variant: SnackBarVariant.ERROR });
      return;
    }

    if (!formData.serviceType) {
      showSnackbar({ message: t("root:pleaseSelectServiceType", "Please select a service type"), variant: SnackBarVariant.ERROR });
      return;
    }

    if (isServiceCategoryRequired && !formData.serviceCategory) {
      showSnackbar({ message: t("root:pleaseSelectServiceCategory", "Please select a service category"), variant: SnackBarVariant.ERROR });
      return;
    }

    if (issues.some(issue => !issue.issueType || !issue.description)) {
      showSnackbar({ message: t("snackBar:pleaseFillInAllIssueDetails"), variant: SnackBarVariant.ERROR });
      return;
    }

    if (!selectedBranchId) {
      showSnackbar({
        message: t("root:unableToDetermineServiceBranch"),
        variant: SnackBarVariant.ERROR
      });
      return;
    }

    const activeQuestions = flowsToRender.flatMap((section) => section.questions);

    for (const question of activeQuestions) {
      const response = questionResponses[question.id] || {};
      const trimmedAnswer = response.answer?.trim();
      const hasAnswer =
        question.answerType === "FILE_UPLOAD"
          ? (response.files || []).length > 0
          : question.answerType === "MULTI_SELECT"
            ? (response.selectedOptions || []).length > 0
            : !!trimmedAnswer;

      if (question.isMandatory && !hasAnswer) {
        showSnackbar({
          message: t("root:serviceQuestionRequired", {
            question: question.question,
            defaultValue: `Please provide an answer for "${question.question}"`
          }),
          variant: SnackBarVariant.ERROR
        });
        return;
      }
    }

    const compiledDetails = activeQuestions.map((question) => {
      const response = questionResponses[question.id] || {};
      const baseDetail: any = {
        questionId: question.id,
        question: question.question,
        answerType: question.answerType,
        options: question.options || [],
        isMandatory: question.isMandatory || false
      };

      switch (question.answerType) {
        case "MULTI_SELECT": {
          const selections = response.selectedOptions || [];
          return {
            ...baseDetail,
            selectedOptions: selections,
            answer: selections.join(", ")
          };
        }
        case "FILE_UPLOAD":
          return {
            ...baseDetail,
            files: response.files || []
          };
        default:
          return {
            ...baseDetail,
            answer: response.answer || ""
          };
      }
    });

    const businessCustomerId = user?.businesscustomers?.id || undefined;
    const individualCustomerId = user?.customerId || undefined;

    let finalServiceCategory = formData.serviceCategory;
    if (
      normalizedServiceType === ServiceRequestType.REGULAR &&
      !finalServiceCategory
    ) {
      const branchesWithSettings =
        branchData?.getSpecificBranches ||
        [];
      const branchIdToUse = vehicleBranchId || selectedBranchId;
      const selectedBranch =
        branchesWithSettings.find((branch: any) => branch.id === branchIdToUse) ||
        consumerWebsite?.branches?.find((branch: any) => branch.id === branchIdToUse);
      
      const regularCategories = selectedBranch?.serviceSettings?.regularServiceCategories || [];
      const defaultCategory =
        regularCategories.find(
          (cat: any) => cat.key === ServiceRequestType.REGULAR
        ) || regularCategories[0];
      if (defaultCategory) {
        finalServiceCategory = defaultCategory.key;
      }
    }

    try {
      const { data } = await createServiceRequest({
        variables: {
          serviceRequest: {
            vehicle: {
              id: formData.vehicle.id,
            },
            issues: issues.map(issue => ({
              issueType: issue.issueType,
              description: issue.description,
              images: issue.images
            })),
            serviceType: normalizedServiceType,
            serviceCategory: finalServiceCategory || undefined,
            comments: formData.comments || undefined,
            additionalDetails: compiledDetails,
            customer: individualCustomerId,
            businessCustomer: businessCustomerId,
            branchId: vehicleBranchId || selectedBranchId,
            jobSheet: jobSheetItems.length > 0 ? jobSheetItems.map(item => ({
              task: item.task,
              taskDescription: item.taskDescription || undefined,
              cost: Math.round((item.cost || 0) * 100)
            })) : undefined
          }
        }
      });

      showSnackbar({
        message: `${t("snackBar:serviceRequestCreatedSuccessfully")} ${data.createServiceRequest.referenceNumber}`,
        variant: SnackBarVariant.SUCCESS
      });
      history.push("/account/service-requests");
    } catch (error: any) {
      showSnackbar({
        message: error.message || t("snackBar:errorCreatingServiceRequest"),
        variant: SnackBarVariant.ERROR
      });
    }
  };


  if (vehicleLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button onClick={() => history.goBack()}>← {t("misc:back")}</Button>
        <Typography variant="h4" style={{ marginLeft: 16 }}>
          {t("root:createNewServiceRequest")}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("root:serviceDetails")}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                {vehicleIdFromUrl && vehicleData?.vehicle ? (
                  <TextField
                    fullWidth
                    label={t("root:selectedVehicle")}
                    value={`${vehicleData.vehicle.licencePlate} - ${vehicleData.vehicle.make} ${vehicleData.vehicle.model}`}
                    disabled
                    variant="outlined"
                  />
                ) : (
                  <FormControl fullWidth required disabled={vehiclesLoading}>
                    <InputLabel>{t("root:serviceRequestSelectVehicle")}</InputLabel>
                    <Select
                      value={formData.vehicle?.id || ""}
                      onChange={(e) => {
                        const selectedVehicle = availableVehicles.find((v: any) => v.id === e.target.value);
                        setFormData((prev) => ({
                          ...prev,
                          vehicle: selectedVehicle
                            ? { id: selectedVehicle.id, licencePlate: selectedVehicle.licencePlate || "" }
                            : null,
                          serviceType: selectedVehicle ? prev.serviceType : "",
                          serviceCategory: selectedVehicle ? prev.serviceCategory : ""
                        }));
                      }}
                    >
                      {vehiclesLoading ? (
                        <MenuItem disabled>{t("root:loadingVehicles")}</MenuItem>
                      ) : availableVehicles.length === 0 ? (
                        <MenuItem disabled>{t("root:noActiveVehiclesFound")}</MenuItem>
                      ) : (
                        availableVehicles.map((vehicle: any) => (
                          <MenuItem key={vehicle.id} value={vehicle.id}>
                            {vehicle.licencePlate} - {vehicle.make} {vehicle.model}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required disabled={!formData.vehicle}>
                  <InputLabel>{t("root:serviceType")}</InputLabel>
                    <Select
                      value={formData.serviceType}
                      disabled={!formData.vehicle}
                      onChange={(e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
                        const value = e.target.value as string;
                        const selectedType = serviceTypeOptions.find(opt => opt.value === value);
                        const normalized = selectedType?.key?.toUpperCase() || value.replace(/\s+/g, "-").toUpperCase();
                        const isRegular = normalized === ServiceRequestType.REGULAR;
                        setFormData((prev) => ({
                          ...prev,
                          serviceType: value,
                          serviceCategory: isRegular ? "" : prev.serviceCategory
                        }));
                        if (!isRegular) {
                          setTemplatesPopulated(false);
                          setJobSheetItems([]);
                        }
                      }}
                  >
                    <MenuItem value="" disabled>
                      {t("root:selectServiceType")}
                    </MenuItem>
                    {serviceTypeOptions.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.vehicle && (
                    <FormHelperText>
                      {t("root:pleaseSelectVehicleFirst", "Please select a vehicle first")}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {formData.serviceType && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required={Boolean(isServiceCategoryRequired)} disabled={!formData.vehicle}>
                    <InputLabel>{t("root:serviceCategory")}</InputLabel>
                    <Select
                      value={formData.serviceCategory}
                      disabled={!formData.vehicle}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          serviceCategory: e.target.value as string
                        }))
                      }
                    >
                      <MenuItem value="" disabled>
                        {t("root:selectServiceCategory")}
                      </MenuItem>
                      {serviceCategoryOptions.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {!formData.vehicle && (
                      <FormHelperText>
                        {t("root:pleaseSelectVehicleFirst", "Please select a vehicle first")}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

      {formData.serviceType && (
        <Card style={{ marginTop: 24 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("root:serviceQuestionnaire")}
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {t(
                "root:serviceQuestionnaireDescription"
              )}
            </Typography>

            {questionsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="160px">
                <CircularProgress />
              </Box>
            ) : isServiceCategoryRequired && !formData.serviceCategory ? (
              <Box
                p={3}
                border="1px dashed #d0d0d0"
                borderRadius={8}
                bgcolor="#fafafa"
                textAlign="center"
              >
                <Typography variant="body2" color="textSecondary">
                  {t(
                    "root:selectServiceCategoryPrompt"
                  )}
                </Typography>
              </Box>
            ) : flowsToRender.length === 0 ? (
              <Box
                p={3}
                border="1px dashed #d0d0d0"
                borderRadius={8}
                bgcolor="#fafafa"
                textAlign="center"
              >
                <Typography variant="body2" color="textSecondary">
                  {t(
                    "root:noServiceQuestionsForType",
                  )}
                </Typography>
              </Box>
            ) : (
              flowsToRender.map((section, sectionIndex) => (
                <Box key={section.key} mt={sectionIndex > 0 ? 3 : 1}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
                    {section.label}
                  </Typography>
                  {section.questions.map((question) => {
                    return (
                      <Box
                        key={question.id}
                        p={2.5}
                        mb={2}
                        border="1px solid #e0e0e0"
                        borderRadius={12}
                        bgcolor="#fcfcfc"
                        boxShadow="inset 0 1px 0 rgba(255,255,255,0.6)"
                      >
                        <Box
                          display="flex"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          style={{ gap: 8 }}
                        >
                          <Typography variant="body1" style={{ fontWeight: 600 }}>
                            {question.question}
                          </Typography>
                          {question.isMandatory && (
                            <Chip label={t("root:required")} size="small" color="secondary" variant="outlined" />
                          )}
                        </Box>
                        {question.helpText && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 4 }}
                          >
                            {question.helpText}
                          </Typography>
                        )}
                        <Box mt={2}>{renderQuestionInput(question)}</Box>
                        {question.answerType === "FILE_UPLOAD" && (
                          <FormHelperText style={{ marginTop: 8 }}>
                            {t("root:fileUploadGuidance")}
                          </FormHelperText>
                        )}
                        {!question.isMandatory && (
                          <FormHelperText style={{ marginTop: 8 }}>
                            {t(
                              "root:optionalServiceQuestion",
                            )}
                          </FormHelperText>
                        )}
                      </Box>
                    );
                  })}
                  {sectionIndex < flowsToRender.length - 1 && <Divider style={{ margin: "16px 0" }} />}
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      )}

        <Card style={{ marginTop: 24 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">{t("root:addDetails")}</Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Add />}
                onClick={handleAddIssue}
              >
                {t("root:addItems")}
              </Button>
            </Box>

            {issues.map((issue, index) => (
              <Box key={index} mb={3} p={2} border="1px solid #e0e0e0" borderRadius={4}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      required
                      label={t("root:issueType")}
                      placeholder={t("root:issueTypePlaceholder")}
                      value={issue.issueType}
                      onChange={(e) => handleIssueChange(index, "issueType", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      required
                      label={t("root:descriptionLabel")}
                      placeholder={t("root:descriptionPlaceholder")}
                      value={issue.description}
                      onChange={(e) => handleIssueChange(index, "description", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id={`image-upload-${index}`}
                      multiple
                      type="file"
                      onChange={(e) => handleImageUpload(index, e)}
                    />
                    <label htmlFor={`image-upload-${index}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={uploadingImages[index] ? <CircularProgress size={20} /> : <CloudUpload />}
                        disabled={uploadingImages[index]}
                      >
                        {uploadingImages[index] ? t("root:uploading") : t("root:upload")}
                      </Button>
                    </label>
                    {issues.length > 1 && (
                      <IconButton 
                        color="secondary" 
                        onClick={() => handleRemoveIssue(index)}
                        style={{ marginLeft: 8 }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Grid>
                  {issue.images.length > 0 && (
                    <Grid item xs={12}>
                      <Box display="flex" flexWrap="wrap" mt={1} style={{ gap: '8px' }}>
                        {issue.images.map((imageKey, imgIndex) => {
                          // s3 key to signedUrl for display
                          const displayUrl = imageUrlMap[imageKey];
                          // load signedUrl
                          if (!imageUrlMap[imageKey]) {
                            getSignedUrl(imageKey).then((url) => {
                              if (url) {
                                setImageUrlMap(prev => ({ ...prev, [imageKey]: url }));
                              }
                            });
                          }
                          return (
                            <Box
                              key={imgIndex}
                              position="relative"
                              width={80}
                              height={80}
                              border="1px solid #e0e0e0"
                              borderRadius={1}
                              overflow="hidden"
                            >
                              <img
                                src={displayUrl || ''}
                                alt={`${t("root:uploaded")} ${imgIndex + 1}`}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                onError={(e) => {
                                  // retry getting signed URL if image fails to load
                                  getSignedUrl(imageKey).then((url) => {
                                    if (url) {
                                      setImageUrlMap(prev => ({ ...prev, [imageKey]: url }));
                                      (e.target as HTMLImageElement).src = url;
                                    }
                                  });
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const newImages = issue.images.filter((_, i) => i !== imgIndex);
                                  handleIssueChange(index, "images", newImages);
                                  setImageUrlMap(prev => {
                                    const updated = { ...prev };
                                    delete updated[imageKey];
                                    return updated;
                                  });
                                }}
                                style={{
                                  position: "absolute",
                                  top: 2,
                                  right: 2,
                                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                                  padding: 4
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          );
                        })}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </CardContent>
        </Card>

        {normalizedServiceType === ServiceRequestType.REGULAR && formData.serviceCategory && (
          <Card style={{ marginTop: 24 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  {t("root:workToBeDone")}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddJobSheetItem}
                >
                  {t("root:addTask")}
                </Button>
              </Box>
              {loadingTemplates ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : jobSheetItems.length === 0 ? (
                <Typography variant="body2" color="textSecondary" style={{ padding: 16 }}>
                  {t("root:noTaskFound")}
                </Typography>
              ) : (
                <>
                  {jobSheetItems.map((item, index) => (
                    <Box
                      key={item.id || index}
                      p={2}
                      mb={2}
                      border="1px solid #e0e0e0"
                      borderRadius={4}
                      bgcolor="#fafafa"
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                          <Autocomplete
                            id={`task-name-autocomplete-${index}`}
                            freeSolo
                            options={serviceTasks}
                            getOptionLabel={(option: any) => (
                              typeof option === 'string' ? option : (option?.name || "")
                            )}
                            onChange={(_: any, newValue: any) => {
                              if (newValue && typeof newValue === "object" && newValue?.name) {
                                setJobSheetItems(prev => {
                                  const updated = [...prev];
                                  updated[index] = {
                                    ...updated[index],
                                    task: newValue.name || "",
                                    taskDescription: newValue.description || "",
                                    cost: (newValue.cost || 0) / 100
                                  };
                                  return updated;
                                });
                                setSelectedTasks({
                                  ...selectedTasks,
                                  [index]: newValue.name || ""
                                });
                                return;
                              }
                              const value = typeof newValue === "string" ? newValue : (newValue?.name || "");
                              handleAutoCompleteInputChange(value, "reset", index);
                              setSelectedTasks({
                                ...selectedTasks,
                                [index]: value
                              });
                            }}
                            onInputChange={(event: any, value: string, reason: string) => {
                              if (reason === "reset") {
                                setSelectedTasks(prev => ({
                                  ...prev,
                                  [index]: value
                                }));
                                return;
                              }
                              handleAutoCompleteInputChange(value, reason, index);
                              setSelectedTasks(prev => ({
                                ...prev,
                                [index]: value
                              }));
                            }}
                            inputValue={selectedTasks[index] || item.task || ""}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                required
                                label={t("root:taskName")}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  style: { height: 40 }
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  style: { padding: "10.5px 14px" }
                                }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label={t("root:descriptionLabel")}
                            value={item.taskDescription || ""}
                            onChange={(e) => handleJobSheetItemChange(index, "taskDescription", e.target.value)}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            label={t("root:cost")}
                            type="text"
                            value={`£${(item.cost || 0).toFixed(2)}`}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value.replace(/£/g, '')) || 0;
                              if (!isNaN(val)) {
                                handleJobSheetItemChange(index, "cost", val);
                              }
                            }}
                            variant="outlined"
                            size="small"
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} md={1}>
                          <IconButton
                            color="secondary"
                            onClick={() => handleRemoveJobSheetItem(index)}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        )}

        <Card style={{ marginTop: 24 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("root:additionalComments")}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder={t("root:additionalCommentsPlaceholder")}
              value={formData.comments}
              onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
            />
          </CardContent>
        </Card>

        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={() => history.goBack()}
            disabled={isSubmitting}
            style={{ marginRight: 16 }}
          >
            {t("misc:cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
          >
            {isSubmitting ? t("root:submitting") : t("root:submitRequest")}
          </Button>
        </Box>
      </form>

      <Box mt={3} p={2} bgcolor="#f5f5f5" borderRadius={4}>
        <Typography variant="body2" color="textSecondary">
          <strong>{t("root:note")}:</strong> {t("root:serviceRequestNote")}
        </Typography>
      </Box>
    </Box>
  );
};

export default CreateServiceRequest;

